import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { PaymentInput, PaymentQueryParams, paymentsService } from '../services/payments.service';

export const usePaymentsPage = (params: PaymentQueryParams) =>
  useQuery({ queryKey: ['payments', 'page', params], queryFn: () => paymentsService.getPage(params), placeholderData: keepPreviousData });

export const usePaymentSummary = () => useQuery({ queryKey: ['payments', 'summary'], queryFn: () => paymentsService.getSummary() });

export const useOutstandingInvoices = (params: { page?: number; limit?: number; search?: string; customerId?: string; overdueOnly?: boolean }, enabled = true) =>
  useQuery({ queryKey: ['payments', 'outstanding', params], queryFn: () => paymentsService.getOutstanding(params), enabled, placeholderData: keepPreviousData });

export const useReceivablesAgeing = (enabled = true) => useQuery({ queryKey: ['payments', 'ageing'], queryFn: () => paymentsService.getAgeing(), enabled });

export const usePayment = (id: string | undefined) =>
  useQuery({ queryKey: ['payments', 'one', id], queryFn: () => paymentsService.getOne(id as string), enabled: !!id });

// A receipt changes what invoices are owed
const useInvalidatePayments = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['payments'] });
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };
};

export const useCreatePayment = () => {
  const invalidate = useInvalidatePayments();
  return useMutation({ mutationFn: (data: PaymentInput) => paymentsService.create(data), onSuccess: invalidate });
};

export const useAllocatePayment = () => {
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: ({ id, allocations }: { id: string; allocations: { invoiceId: string; amount: number }[] }) => paymentsService.allocate(id, allocations),
    onSuccess: invalidate,
  });
};

export const useCancelPayment = () => {
  const invalidate = useInvalidatePayments();
  return useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => paymentsService.cancel(id, reason), onSuccess: invalidate });
};
